import { useIntl } from 'react-intl';
import React, { useMemo } from 'react';
import { Form, FormInstance } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { Label } from 'ui/Label';
import { Input } from 'ui/Input';
import { InputNumber } from 'ui/InputNumber';
import { DatePicker } from 'ui/DatePicker';
import { Checkbox } from 'ui/Checkbox';
import { Button } from 'ui/Button';
import { Select, SelectOption, LabelValueType } from 'ui/Select';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import EnumSelect from '../../../../admin/Selects/EnumSelect';
import {
  ListingProduct,
  DetailedDeliveryData,
  DetailedDeliveryFormValues,
  UnusedProduct,
  SplitDeliveryListingProduct,
} from '../../../../types/ListingData';

interface DetailedDeliveryFormContentProps {
  form: FormInstance;
  isEditMode: boolean;
  editingDelivery: DetailedDeliveryData | null;
  selectedProducts: ListingProduct[];
  isAllProducts: boolean;
  isAllQuantities: boolean;
  availableProducts: ListingProduct[];
  onAllProductsChange: (checked: boolean) => void;
  onAllQuantitiesChange: (checked: boolean) => void;
  onProductSelectionChange: (categoryIds: number[]) => void;
  onFormSubmit: (values: DetailedDeliveryFormValues) => void;
  onFormCancel: () => void;
  getAvailableProducts: () => ListingProduct[];
  calculateUnusedProducts: (
    excludeDelivery?: DetailedDeliveryData
  ) => UnusedProduct[];
  splitDeliveriesMode?: {
    enabled: boolean;
    availableApplications: {
      id: number;
      name: string;
      farm_name?: string;
    }[];
    selectedApplicationId?: number | null;
    onApplicationChange?: (applicationId: number | null) => void;
  };
}

export const DetailedDeliveryFormContent = ({
  form,
  isEditMode,
  editingDelivery,
  selectedProducts,
  isAllProducts,
  isAllQuantities,
  availableProducts,
  onAllProductsChange,
  onAllQuantitiesChange,
  onProductSelectionChange,
  onFormSubmit,
  onFormCancel,
  getAvailableProducts,
  calculateUnusedProducts,
  splitDeliveriesMode,
}: DetailedDeliveryFormContentProps) => {
  const intl = useIntl();
  const formSelectedProducts = useWatch('selected_products', form);
  const selectedApplicationId =
    splitDeliveriesMode?.selectedApplicationId ?? null;

  const handleApplicationChange = (applicationId: number | null) => {
    splitDeliveriesMode?.onApplicationChange?.(applicationId);

    form.setFieldValue('selected_products', []);
    onProductSelectionChange([]);
  };

  const handleFormSubmit = (values: DetailedDeliveryFormValues) => {
    onFormSubmit(values);
  };

  const normalizeSelectValue = () => {
    if (
      formSelectedProducts &&
      Array.isArray(formSelectedProducts) &&
      formSelectedProducts.length > 0
    ) {
      const firstItem = formSelectedProducts[0];
      if (
        typeof firstItem === 'object' &&
        firstItem !== null &&
        'label' in firstItem
      ) {
        return formSelectedProducts;
      } else {
        return formSelectedProducts
          .map((id: number) => {
            const product = availableProducts.find((p) => p.category_id === id);
            return {
              value: id,
              label: product?.name || product?.category?.name || '',
            };
          })
          .filter((item) => !!item.value);
      }
    }

    return selectedProducts
      .map((p) => ({
        value: p.category_id,
        label: p.name || p.category?.name || '',
      }))
      .filter((item) => !!item.value);
  };

  const selectValue = normalizeSelectValue();

  const filteredAvailableProducts = useMemo(() => {
    const withQuantity = getAvailableProducts();

    if (!splitDeliveriesMode?.enabled || !selectedApplicationId) {
      return withQuantity;
    }

    return withQuantity.filter((product) => {
      const extendedProduct = product as SplitDeliveryListingProduct;
      return extendedProduct._applications?.some(
        (app: {
          applicationId: number;
          applicationProductId: number;
          quantity: number;
          offeredPrice: number;
          farmName?: string;
          applicationName?: string;
        }) => app.applicationId === selectedApplicationId
      );
    });
  }, [
    getAvailableProducts,
    splitDeliveriesMode?.enabled,
    selectedApplicationId,
  ]);

  return (
    <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
      {splitDeliveriesMode?.enabled && (
        <div className="form-row">
          <div className="form-field">
            <Label
              label={intl.formatMessage({
                id: 'listings.form.select_application',
              })}
            />
            <Select
              value={selectedApplicationId || undefined}
              onChange={(value) => handleApplicationChange(value || null)}
              placeholder={intl.formatMessage({
                id: 'listings.form.select_application_placeholder',
              })}
            >
              {splitDeliveriesMode.availableApplications.map((application) => (
                <SelectOption key={application.id} value={application.id}>
                  {application.name}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>
      )}

      <div className="form-row">
        <div className="form-field">
          <Label
            label={intl.formatMessage({
              id: splitDeliveriesMode?.enabled
                ? 'listings.form.select_products_split_deliveries'
                : 'listings.form.select_products',
            })}
          />
          <Select
            name="selected_products"
            mode="multiple"
            value={selectValue}
            onChange={(values: (string | number | LabelValueType)[]) => {
              const categoryIds = values.map((v) =>
                typeof v === 'object' && v !== null
                  ? (v as LabelValueType).value
                  : v
              );
              onProductSelectionChange(categoryIds as number[]);
            }}
            disabled={
              isAllProducts ||
              (splitDeliveriesMode?.enabled && !selectedApplicationId)
            }
            labelInValue={true}
            validations={'required'}
          >
            {Array.from(
              new Map(
                filteredAvailableProducts.map((product) => [
                  product.category_id,
                  {
                    id: product.category_id,
                    name: product.name || product.category?.name || '',
                    applicationInfo:
                      splitDeliveriesMode?.enabled && selectedApplicationId
                        ? splitDeliveriesMode.availableApplications.find(
                            (app) => app.id === selectedApplicationId
                          )?.name
                        : undefined,
                  },
                ])
              ).values()
            ).map(
              (category: {
                id: number;
                name: string;
                applicationInfo?: string;
              }) => (
                <SelectOption
                  key={category.id}
                  value={category.id}
                  label={category.name}
                >
                  {category.name}
                </SelectOption>
              )
            )}
          </Select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field checkbox-field">
          <Checkbox
            name="is_all_products"
            checked={isAllProducts}
            onChange={(e) => onAllProductsChange(e.target.checked)}
            label={intl.formatMessage({
              id: 'listings.form.is_all_products',
            })}
          />
          <Checkbox
            name="is_full_quantity"
            checked={isAllQuantities}
            onChange={(e) => onAllQuantitiesChange(e.target.checked)}
            label={intl.formatMessage({
              id: 'listings.form.is_all_quantities',
            })}
          />
        </div>
      </div>

      {selectedProducts.length > 0 && (
        <div className="form-row">
          <div className="form-field">
            <div className="selected-products">
              {(() => {
                const unusedProducts =
                  isEditMode && editingDelivery
                    ? calculateUnusedProducts(editingDelivery)
                    : calculateUnusedProducts();

                return selectedProducts.map((product) => {
                  const productName =
                    product.name ||
                    product.category?.name ||
                    `Product ${product.id}`;
                  const unusedProduct = unusedProducts.find(
                    (unused) => unused.name === productName
                  );
                  const availableQuantity = unusedProduct?.quantity || 0;

                  return (
                    <div
                      key={`product-quantity-${product.id}`}
                      className="product-quantity-row"
                    >
                      <div className="product-info">
                        <span className="product-name">
                          {product.name || product.category?.name}
                        </span>
                      </div>
                      <div className="quantity-input">
                        <Label
                          label={intl.formatMessage(
                            {
                              id: 'listings.form.total_quantity',
                            },
                            {
                              required_quantity: availableQuantity,
                              unit_short:
                                product.storage_unit?.short ||
                                product.storage_unit?.name ||
                                'kg.',
                            }
                          )}
                        />
                        <Input
                          key={`quantity-input-${product.category_id || product.id}`}
                          type="number"
                          required={true}
                          disabled={isAllQuantities}
                          name={[
                            'product_quantities',
                            product.category_id || product.id!,
                          ]}
                          validations={
                            !isAllQuantities ? 'required' : undefined
                          }
                          placeholder={intl.formatMessage(
                            {
                              id: 'listings.form.input_delivery_quantity',
                            },
                            {
                              unit_short:
                                product.storage_unit?.short ||
                                product.storage_unit?.name ||
                                'kg.',
                            }
                          )}
                        />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="form-row">
        <div className="form-field">
          <Label
            label={intl.formatMessage({
              id: 'listings.form.delivery_address',
            })}
          />
          <WithoutModalAddressFinder
            form={form}
            name="delivery_address"
            disabled={false}
            deliveryTypeValue="delivery"
            validations="required"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <Label
            label={intl.formatMessage({
              id: 'listings.form.delivery_start_date',
            })}
          />
          <DatePicker name="delivery_start_date" />
        </div>
        <div className="form-field">
          <Label
            label={intl.formatMessage({
              id: 'listings.form.delivery_end_date',
            })}
          />
          <DatePicker
            name="delivery_end_date"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue('delivery_start_date');
                  if (!value || !startDate) {
                    return Promise.resolve();
                  }
                  if (value.isBefore(startDate) || value.isSame(startDate)) {
                    return Promise.reject(
                      new Error(
                        intl.formatMessage({
                          id: 'validation.end_date_after_start',
                        })
                      )
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <div className="quantity-and-unit">
            <div className="quantity-input">
              <InputNumber
                name="delivery_frequency_value"
                label={intl.formatMessage({
                  id: 'listings.form.delivery_frequency',
                })}
                controls={false}
              />
            </div>
            <EnumSelect
              name="delivery_frequency_unit"
              code="ListingDeliveryFrequencyUnit"
            />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <Button
          className="cancel-button"
          label={intl.formatMessage({ id: 'general.cancel' })}
          onClick={onFormCancel}
        />
        <Button
          className="add-button"
          type={'primary'}
          label={
            isEditMode
              ? intl.formatMessage({ id: 'general.update' })
              : intl.formatMessage({ id: 'general.add' })
          }
          onClick={() => form.submit()}
        />
      </div>
    </Form>
  );
};
